import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../store/auth-context';
import { authApi, ApiError } from '../../services/api';
import { mapAuthError } from '../../utils/auth-error-messages';
import {
  getRememberedEmail,
  rememberEmail,
} from '../../utils/remembered-email';
import { shouldDefaultToDeviceSignIn } from '../../utils/constrained-device';
import { SignInView, type SignInStep } from './SignInView';

const signupEnabled = import.meta.env.VITE_SIGNUP_ENABLED === 'true';

export default function SignIn() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<SignInStep>('email');
  const [email, setEmail] = useState(() => getRememberedEmail());
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Set after we redirect back from /signin/reset so the alert slot shows a
  // confirmation instead of an error. Cleared as soon as the user types or
  // submits.
  const [notice, setNotice] = useState('');

  const { signIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep the query string — deep links like /device?user_code=… must survive
  // the sign-in round-trip.
  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search || ''}`
    : '/';

  // Input-constrained browsers (Tesla, TVs) default to the QR device-login
  // flow. `?device=1|0` forces/suppresses it; picking "Sign in with email"
  // over there records a sticky opt-out. `from` rides along so deep links
  // still land after the cross-device sign-in.
  useEffect(() => {
    if (shouldDefaultToDeviceSignIn(searchParams.get('device'))) {
      navigate('/signin/device', { replace: true, state: { from: fromLocation } });
    }
    // Mount-only: UA and opt-out don't change mid-visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface ?error=... from the Okta callback, then strip it from the URL.
  useEffect(() => {
    const code = searchParams.get('error');
    if (code) {
      setError(mapAuthError(code));
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Confirmation banner after a successful cookie reset (/signin/reset → here).
  useEffect(() => {
    if (searchParams.get('reset') === '1') {
      setNotice('Browser cookies cleared. Try signing in again.');
      const next = new URLSearchParams(searchParams);
      next.delete('reset');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Navigate away when authenticated (e.g. cross-tab login broadcast or
  // returning from a successful Okta callback).
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter your email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);

    try {
      // Pass the deep link through the federated flow — the OAuth callback
      // redirects to it after sign-in.
      const result = await authApi.discover(email, from !== '/' ? from : undefined);
      rememberEmail(trimmed);
      if (result.method === 'okta' && result.authorizeUrl) {
        setStep('redirecting');
        window.location.href = result.authorizeUrl;
        return;
      }
      setStep('password');
    } catch (err) {
      if (err instanceof ApiError && err.status === 431) {
        // Sentinel — SignInView renders the rich "clear cookies" variant.
        setError('cookies-too-large');
      } else if (err instanceof ApiError) {
        setError("Couldn't reach the sign-in service. Please try again.");
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setIsLoading(true);

    try {
      await signIn({ email, password });
      navigate(from, { replace: true });
    } catch {
      setError('Invalid email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  const useDifferentEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
    setNotice('');
  };

  return (
    <SignInView
      step={step}
      email={email}
      password={password}
      error={error}
      notice={notice}
      isLoading={isLoading}
      signupEnabled={signupEnabled}
      onEmailChange={(v) => {
        setEmail(v);
        if (error) setError('');
        if (notice) setNotice('');
      }}
      onPasswordChange={(v) => {
        setPassword(v);
        if (error) setError('');
        if (notice) setNotice('');
      }}
      onEmailSubmit={handleEmailSubmit}
      onPasswordSubmit={handlePasswordSubmit}
      onUseDifferentEmail={useDifferentEmail}
    />
  );
}
